// analytics-reservation.ts
import type { Reservation } from '../apis/models/reservations/res/Reservation';
import type { ProductOption } from '../apis/models/products/res/ProductOption';
import {
  getProductOptionPrice,
  getProductOptionTotalPrice,
  getProductOptionTotalTax,
} from './utils';
import { trackGaEvent } from './ga';

/**
 * 로그인
 * GA4 권장 이벤트: login
 */
export const sendLoginEvent = () => {
  trackGaEvent('login', {
    method: 'SMS인증',
  });
};

/**
 * 회원가입
 * GA4 권장 이벤트: sign_up
 */
export const sendSignUpEvent = (method: string = 'SMS인증') => {
  trackGaEvent('sign_up', {
    method,
  });
};

/**
 * 상품 상세 보기 (옵션 단위)
 * GA4 권장 이벤트: view_item
 */
export const sendViewItemEvent = (productOption: ProductOption) => {
  trackGaEvent('view_item', {
    currency: 'KRW',
    value: getProductOptionPrice(productOption),
    items: mapProductOptionsToGAItems([productOption]),
  });
};

/**
 * 장바구니 등록
 * GA4 권장 이벤트: add_to_cart
 */
export const sendAddToCartEvent = (productOptions: ProductOption[]) => {
  trackGaEvent('add_to_cart', {
    currency: 'KRW',
    value: getProductOptionTotalPrice(productOptions),
    items: mapProductOptionsToGAItems(productOptions),
  });
};

/**
 * 장바구니 제거
 * GA4 권장 이벤트: remove_from_cart
 */
export const sendRemoveFromCartEvent = (productOptions: ProductOption[]) => {
  trackGaEvent('remove_from_cart', {
    currency: 'KRW',
    value: getProductOptionTotalPrice(productOptions),
    items: mapProductOptionsToGAItems(productOptions),
  });
};

/**
 * 장바구니 화면 진입
 * GA4 권장 이벤트: view_cart
 */
export const sendViewCartEvent = (productOptions: ProductOption[]) => {
  trackGaEvent('view_cart', {
    currency: 'KRW',
    value: getProductOptionTotalPrice(productOptions),
    items: mapProductOptionsToGAItems(productOptions),
  });
};

/**
 * 결제 플로우 시작
 * GA4 권장 이벤트: begin_checkout
 */
export const sendBeginCheckoutEvent = (productOptions: ProductOption[]) => {
  trackGaEvent('begin_checkout', {
    currency: 'KRW',
    value: getProductOptionTotalPrice(productOptions),
    items: mapProductOptionsToGAItems(productOptions),
  });
};

/**
 * 예약 완료 = 구매 완료
 * GA4 권장 이벤트: purchase
 */
export const sendPurchaseEvent = (
  reservation: Reservation,
  productOptions: ProductOption[],
) => {
  const { id } = reservation;

  trackGaEvent('purchase', {
    transaction_id: id,
    currency: 'KRW',
    value: getProductOptionTotalPrice(productOptions),
    tax: getProductOptionTotalTax(productOptions),
    items:
      productOptions.length > 0
        ? mapProductOptionsToGAItems(productOptions)
        : [
            {
              item_id: `방문상담`,
              item_name: '방문상담',
              index: 0,
              price: 0,
              item_category: '방문상담',
              item_category2: '방문상담',
              item_category3: '방문상담',
            },
          ],
  });
};

/**
 * 환불
 * GA4 권장 이벤트: refund
 * - 전체 환불 기준 (부분 환불이면 value/items를 상황에 맞게 조정)
 */
export const sendRefundEvent = (
  reservationId: number,
  value: number,
  productOptions?: ProductOption[],
) => {
  trackGaEvent('refund', {
    transaction_id: reservationId,
    value,
    currency: 'KRW',
    ...(productOptions && productOptions.length > 0
      ? { items: mapProductOptionsToGAItems(productOptions) }
      : {}),
  });
};

/**
 * 프로모션 노출
 * GA4 권장 이벤트: view_promotion
 * - 프로모션 배너/이벤트 노출 시 사용 (items는 선택)
 */
export const sendViewPromotionEvent = (params: {
  promotionId?: number | string;
  promotionName?: string;
  creativeName?: string;
  creativeSlot?: string;
  /** 프로모션이 연결된 상품 옵션들 (선택) */
  productOptions?: ProductOption[];
}) => {
  const {
    promotionId,
    promotionName,
    creativeName,
    creativeSlot,
    productOptions,
  } = params;

  trackGaEvent('view_promotion', {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_name: creativeName,
    creative_slot: creativeSlot,
    ...(productOptions && productOptions.length > 0
      ? { items: mapProductOptionsToGAItems(productOptions) }
      : {}),
  });
};

/**
 * ⭐ 프로모션 클릭 (URL 파라미터로 들어오는 경우 포함)
 */
export const sendSelectPromotionEvent = (params: {
  promotionId: number | string;
  promotionName?: string;
  creativeName?: string;
  creativeSlot?: string;
  items?: ProductOption[];
}) => {
  const { promotionId, promotionName, creativeName, creativeSlot, items } =
    params;

  trackGaEvent('select_promotion', {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_name: creativeName,
    creative_slot: creativeSlot,
    ...(items ? { items: mapProductOptionsToGAItems(items) } : {}),
  });
};

/**
 * 전체 상품 리스트 노출 (예약 플로우 진입 시점 등)
 * - item_list_id, item_list_name, items 전부 없이 가볍게 기록
 */
export const sendViewItemListEvent = ({
  itemListName,
}: {
  itemListName: string;
}) => {
  trackGaEvent('view_item_list', {
    item_list_name: itemListName,
  });
};

const mapProductOptionsToGAItems = (
  productOptions: ProductOption[],
): {
  // 상품 구분자 {product_option_id}
  item_id: string;
  // 상품명
  item_name: string;
  // 배열 인덱스
  index: number;
  // 상품 옵션 카테고리 구분자
  item_category: 'category' | 'event' | string;
  // 상품 옵션 카테고리 ,로 구분
  item_category2: string;
  // 상품명
  item_category3: string;
  // 가격
  price: number;
}[] => {
  return productOptions.map((productOption, index) => {
    const { productName, id, productOptionName, categoryName, eventName } =
      productOption;
    const price = getProductOptionPrice(productOption);

    return {
      item_id: `${id}`,
      item_name: productOptionName,
      index:
        typeof productOption.displayIndex === 'number'
          ? productOption.displayIndex
          : index,
      item_category: categoryName ? 'category' : 'event',
      item_category2: categoryName || eventName || '',
      item_category3: productName || '',
      price,
    };
  });
};
